import { Grid } from '~/engine/data/common/Grid';
import type { ContourStepMove } from '~/domain/undo/ContourEditStep';
import { DicomColor } from '../dicom/dicomColor';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import {
    RoiGenerationAlgorithmType,
    RoiInterpretedType,
} from '../dicom/dicomstructureset';
import { BaseDomainEvent } from './DomainEvent';
import { EventSource } from './EventTypes';

export type RoiPropertyUpdates = {
    name?: string;
    color?: DicomColor;
    visible?: boolean;
    locked?: boolean;
    approvalStatus?: DICOMApprovalStatus;
    contourThickness?: number;
    interpretedType?: RoiInterpretedType;
    electronDensityOverride?: number | undefined;
    massDensityOverride?: number | undefined;
    roiDescription?: string;
    roiGenerationAlgorithm?: RoiGenerationAlgorithmType;
    deleted?: boolean;
};

/**
 * Domain event emitted when {@link Roi} creation is requested
 */
export class RoiCreationRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_CREATION_REQUESTED';

    constructor(
        structureSetUid: string,
        source: EventSource = 'user',
        interpretedType?: RoiInterpretedType,
        initialCoordinatesMm?: { x: number; y: number; z: number }
    ) {
        super(
            RoiCreationRequestedEvent.EVENT_TYPE,
            `roi-creation-for-${structureSetUid}`,
            {
                structureSetUid,
                source,
                interpretedType,
                initialCoordinatesMm,
            }
        );
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get source(): EventSource {
        return this.data.source;
    }

    get interpretedType(): RoiInterpretedType | undefined {
        return this.data.interpretedType;
    }

    get initialCoordinatesMm():
        | { x: number; y: number; z: number }
        | undefined {
        return this.data.initialCoordinatesMm;
    }
}

/**
 * Domain event emitted when {@link Roi} selection is requested
 */
export class RoiSelectionRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_SELECTION_REQUESTED';

    constructor(roiNumber: number, source: EventSource = 'user') {
        super(RoiSelectionRequestedEvent.EVENT_TYPE, `roi-${roiNumber}`, {
            roiNumber,
            source,
        });
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} deletion is requested
 */
export class RoiDeletionRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_DELETION_REQUESTED';

    constructor(
        structureSetUid: string,
        roiNumber: number,
        source: EventSource = 'user'
    ) {
        super(RoiDeletionRequestedEvent.EVENT_TYPE, `roi-${roiNumber}`, {
            structureSetUid,
            roiNumber,
            source,
        });
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} algorithm change is requested
 */
export class RoiAlgorithmChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_ALGORITHM_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        currentAlgorithm: RoiGenerationAlgorithmType,
        requestedAlgorithm: RoiGenerationAlgorithmType,
        reason: string
    ) {
        super(RoiAlgorithmChangeRequestedEvent.EVENT_TYPE, `roi-${roiNumber}`, {
            roiNumber,
            currentAlgorithm,
            requestedAlgorithm,
            reason,
        });
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }

    get currentAlgorithm(): RoiGenerationAlgorithmType {
        return this.data.currentAlgorithm;
    }

    get requestedAlgorithm(): RoiGenerationAlgorithmType {
        return this.data.requestedAlgorithm;
    }

    get reason(): string {
        return this.data.reason;
    }
}

/**
 * Domain event emitted when {@link Roi} visibility change is requested
 */
export class RoiVisibilityChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_VISIBILITY_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        visible: boolean,
        source: EventSource = 'user'
    ) {
        super(
            RoiVisibilityChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiNumber}`,
            { roiNumber, visible, source }
        );
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get visible(): boolean {
        return this.data.visible;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when a {@link Roi} edit-lock change is requested.
 */
export class RoiLockChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_LOCK_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        locked: boolean,
        source: EventSource = 'user'
    ) {
        super(RoiLockChangeRequestedEvent.EVENT_TYPE, `roi-${roiNumber}`, {
            roiNumber,
            locked,
            source,
        });
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get locked(): boolean {
        return this.data.locked;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} name change is requested
 */
export class RoiNameChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_NAME_CHANGE_REQUESTED';

    constructor(roiNumber: number, name: string, source: EventSource = 'user') {
        super(RoiNameChangeRequestedEvent.EVENT_TYPE, `roi-${roiNumber}`, {
            roiNumber,
            name,
            source,
        });
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get name(): string {
        return this.data.name;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} color change is requested
 */
export class RoiColorChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_COLOR_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        color: DicomColor,
        source: EventSource = 'user'
    ) {
        super(RoiColorChangeRequestedEvent.EVENT_TYPE, `roi-${roiNumber}`, {
            roiNumber,
            color,
            source,
        });
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get color(): DicomColor {
        return this.data.color;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} contour thickness change is requested
 */
export class RoiContourThicknessChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_CONTOUR_THICKNESS_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        contourThickness: number,
        source: EventSource = 'user'
    ) {
        super(
            RoiContourThicknessChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiNumber}`,
            { roiNumber, contourThickness, source }
        );
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get contourThickness(): number {
        return this.data.contourThickness;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} approval status change is requested
 */
export class RoiApprovalStatusChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_APPROVAL_STATUS_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        approvalStatus: DICOMApprovalStatus,
        reviewerName: string,
        source: EventSource = 'user'
    ) {
        super(
            RoiApprovalStatusChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiNumber}`,
            { roiNumber, approvalStatus, reviewerName, source }
        );
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get approvalStatus(): DICOMApprovalStatus {
        return this.data.approvalStatus;
    }
    get reviewerName(): string {
        return this.data.reviewerName;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} interpreted type change is requested
 */
export class RoiInterpretedTypeChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_INTERPRETED_TYPE_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        interpretedType: RoiInterpretedType,
        source: EventSource = 'user'
    ) {
        super(
            RoiInterpretedTypeChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiNumber}`,
            { roiNumber, interpretedType, source }
        );
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get interpretedType(): RoiInterpretedType {
        return this.data.interpretedType;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when an {@link Roi} electron density override change
 * is requested.
 */
export class RoiElectronDensityOverrideChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE =
        'ROI_ELECTRON_DENSITY_OVERRIDE_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        electronDensityOverride: number | undefined,
        source: EventSource = 'user'
    ) {
        super(
            RoiElectronDensityOverrideChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiNumber}`,
            { roiNumber, electronDensityOverride, source }
        );
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get electronDensityOverride(): number | undefined {
        return this.data.electronDensityOverride;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when an {@link Roi} mass density override change is
 * requested.
 */
export class RoiMassDensityOverrideChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_MASS_DENSITY_OVERRIDE_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        massDensityOverride: number | undefined,
        source: EventSource = 'user'
    ) {
        super(
            RoiMassDensityOverrideChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiNumber}`,
            { roiNumber, massDensityOverride, source }
        );
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get massDensityOverride(): number | undefined {
        return this.data.massDensityOverride;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} description change is requested
 */
export class RoiDescriptionChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_DESCRIPTION_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        roiDescription: string,
        source: EventSource = 'user'
    ) {
        super(
            RoiDescriptionChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiNumber}`,
            { roiNumber, roiDescription, source }
        );
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get roiDescription(): string {
        return this.data.roiDescription;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} generation algorithm change is requested
 */
export class RoiGenerationAlgorithmChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_GENERATION_ALGORITHM_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        roiGenerationAlgorithm: RoiGenerationAlgorithmType,
        source: EventSource = 'user'
    ) {
        super(
            RoiGenerationAlgorithmChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiNumber}`,
            { roiNumber, roiGenerationAlgorithm, source }
        );
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get roiGenerationAlgorithm(): RoiGenerationAlgorithmType {
        return this.data.roiGenerationAlgorithm;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} contour modification is started
 */
export class RoiContourModificationStartedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_CONTOUR_MODIFICATION_STARTED';

    constructor(
        roiNumber: number,
        operation: string,
        pixels: Uint8Array[],
        grid: Grid,
        source: EventSource = 'user'
    ) {
        super(
            RoiContourModificationStartedEvent.EVENT_TYPE,
            `roi-${roiNumber}`,
            {
                roiNumber,
                operation,
                pixels,
                grid,
                source,
            }
        );
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }

    get operation(): string {
        return this.data.operation;
    }

    get pixels(): Uint8Array[] {
        return this.data.pixels;
    }

    get grid(): Grid {
        return this.data.grid;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * `modificationType` value of the body-contour generation write
 * (SR-WOR-H-27-1). Writes carrying this label AUTHOR the ROI's contours
 * (threshold extraction) rather than editing them, so the
 * AUTOMATIC → SEMI_AUTOMATIC demotion on contour edits must not apply —
 * the same operation declares AUTOMATIC as part of the apply.
 */
export const BODY_CONTOUR_OPERATION = 'Body contour';

/**
 * Domain event emitted when {@link Roi} contours are modified
 */
export class RoiContoursModifiedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_CONTOURS_MODIFIED';

    constructor(
        structureSetUid: string,
        roiNumber: number,
        modificationType: string,
        pixels: Uint8Array[],
        grid: Grid,
        initialPixels?: Uint8Array[],
        initialGrid?: Grid
    ) {
        // ROINumber is 1-based per structure set, so the owning structure set
        // UID is part of the identity: undo capture and previews must
        // attribute the edit to its own set, never to a same-numbered ROI in
        // whichever set happens to be selected.
        super(
            RoiContoursModifiedEvent.EVENT_TYPE,
            `roi-${structureSetUid}-${roiNumber}`,
            {
                structureSetUid,
                roiNumber,
                modificationType,
                pixels,
                grid,
                initialPixels,
                initialGrid,
            }
        );
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }

    get modificationType(): string {
        return this.data.modificationType;
    }

    get pixels(): Uint8Array[] {
        return this.data.pixels;
    }

    get grid(): Grid {
        return this.data.grid;
    }

    get initialPixels(): Uint8Array[] | undefined {
        return this.data.initialPixels;
    }

    get initialGrid(): Grid | undefined {
        return this.data.initialGrid;
    }
}

/**
 * Domain event emitted when {@link Roi} contour undo is requested
 */
export class ContourUndoRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'CONTOUR_UNDO_REQUESTED';

    constructor(
        structureSetUid: string,
        roiNumber: number,
        compressedPixels: Uint8Array,
        grid: Grid,
        source: EventSource = 'user',
        /** The history steps the restore left and restored, when the publisher knows them. */
        steps?: ContourStepMove
    ) {
        // ROINumber is 1-based per structure set, so the owning structure set
        // UID is part of the identity: an undo must reach only its own ROI.
        super(
            ContourUndoRequestedEvent.EVENT_TYPE,
            `roi-${structureSetUid}-${roiNumber}`,
            {
                structureSetUid,
                roiNumber,
                compressedPixels,
                grid,
                source,
                steps,
            }
        );
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }

    get compressedPixels(): Uint8Array {
        return this.data.compressedPixels;
    }

    get grid(): Grid {
        return this.data.grid;
    }

    get source(): EventSource {
        return this.data.source;
    }

    get steps(): ContourStepMove | undefined {
        return this.data.steps;
    }
}

/**
 * Domain event emitted when {@link Roi} contour redo is requested
 */
export class ContourRedoRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'CONTOUR_REDO_REQUESTED';

    constructor(
        structureSetUid: string,
        roiNumber: number,
        compressedPixels: Uint8Array,
        grid: Grid,
        source: EventSource = 'user',
        /** The history steps the restore left and restored, when the publisher knows them. */
        steps?: ContourStepMove
    ) {
        // ROINumber is 1-based per structure set, so the owning structure set
        // UID is part of the identity: a redo must reach only its own ROI.
        super(
            ContourRedoRequestedEvent.EVENT_TYPE,
            `roi-${structureSetUid}-${roiNumber}`,
            {
                structureSetUid,
                roiNumber,
                compressedPixels,
                grid,
                source,
                steps,
            }
        );
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }

    get compressedPixels(): Uint8Array {
        return this.data.compressedPixels;
    }

    get grid(): Grid {
        return this.data.grid;
    }

    get source(): EventSource {
        return this.data.source;
    }

    get steps(): ContourStepMove | undefined {
        return this.data.steps;
    }
}

/**
 * The domain events that mutate a single ROI's contour SDF in place. A brush /
 * edit stroke emits a {@link RoiContoursModifiedEvent}, while undo and redo
 * restore the pixels through {@link ContourUndoRequestedEvent} /
 * {@link ContourRedoRequestedEvent} WITHOUT re-emitting a modified event. All
 * three carry a `roiNumber`, so any overlay, preview, or read-out keyed on a
 * ROI's live texture must observe all three or it will go stale after an edit
 * or an undo/redo.
 */
export type RoiContourEditEvent =
    | RoiContoursModifiedEvent
    | ContourUndoRequestedEvent
    | ContourRedoRequestedEvent;

/**
 * The event types of every {@link RoiContourEditEvent}: subscribe to all of
 * them to observe each change to a ROI's contour SDF, including undo and redo.
 */
export const ROI_CONTOUR_EDIT_EVENT_TYPES: readonly string[] = [
    RoiContoursModifiedEvent.EVENT_TYPE,
    ContourUndoRequestedEvent.EVENT_TYPE,
    ContourRedoRequestedEvent.EVENT_TYPE,
];

/**
 * Domain event emitted when {@link Roi} properties are updated
 */
export class RoiPropertiesUpdatedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_PROPERTIES_UPDATED';

    constructor(
        structureSetUid: string,
        roiNumber: number,
        updatedProperties: RoiPropertyUpdates,
        updateSource: EventSource
    ) {
        // ROINumber is 1-based per structure set, so the owning structure set
        // UID is part of the identity: an update must reach only its own ROI.
        super(
            RoiPropertiesUpdatedEvent.EVENT_TYPE,
            `roi-${structureSetUid}-${roiNumber}`,
            {
                structureSetUid,
                roiNumber,
                updatedProperties,
                updateSource,
            }
        );
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }

    get updatedProperties(): RoiPropertyUpdates {
        return this.data.updatedProperties;
    }

    get updateSource(): EventSource {
        return this.data.updateSource;
    }
}

/**
 * Domain event emitted when {@link Roi} is created
 */
export class RoiCreatedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_CREATED';

    constructor(
        structureSetUid: string,
        roiNumber: number,
        source: EventSource = 'system'
    ) {
        super(
            RoiCreatedEvent.EVENT_TYPE,
            `roi-${roiNumber}-for-${structureSetUid}`,
            {
                structureSetUid,
                roiNumber,
                source,
            }
        );
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} point coordinates change is requested.
 * Used for repositioning POINT-type ROIs (markers, isocenters, registrations).
 * Coordinates are in patient space (mm).
 */
export class RoiPointCoordinatesChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_POINT_COORDINATES_CHANGE_REQUESTED';

    constructor(
        roiNumber: number,
        coordinates: { x: number; y: number; z: number },
        source: EventSource = 'user'
    ) {
        super(
            RoiPointCoordinatesChangeRequestedEvent.EVENT_TYPE,
            `roi-${roiNumber}`,
            { roiNumber, coordinates, source }
        );
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }
    get coordinates(): { x: number; y: number; z: number } {
        return this.data.coordinates;
    }
    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link Roi} copy is requested
 */
export class RoiCopyRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_COPY_REQUESTED';

    constructor(
        structureSetUid: string,
        roiNumber: number,
        source: EventSource
    ) {
        super(
            RoiCopyRequestedEvent.EVENT_TYPE,
            `roi-${roiNumber}-for-${structureSetUid}`,
            {
                structureSetUid,
                roiNumber,
                source,
            }
        );
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get roiNumber(): number {
        return this.data.roiNumber;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when SDF copy-over from one {@link Roi}
 * to another is requested.
 */
export class RoiSdfCopyOverRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'ROI_SDF_COPY_OVER_REQUESTED';

    constructor(
        structureSetUid: string,
        sourceRoiNumber: number,
        targetRoiNumber: number,
        source: EventSource
    ) {
        super(
            RoiSdfCopyOverRequestedEvent.EVENT_TYPE,
            `roi-sdf-copy-${sourceRoiNumber}-to-${targetRoiNumber}-for-${structureSetUid}`,
            {
                structureSetUid,
                sourceRoiNumber,
                targetRoiNumber,
                source,
            }
        );
    }

    get structureSetUid(): string {
        return this.data.structureSetUid;
    }

    get sourceRoiNumber(): number {
        return this.data.sourceRoiNumber;
    }

    get targetRoiNumber(): number {
        return this.data.targetRoiNumber;
    }

    get source(): EventSource {
        return this.data.source;
    }
}
